/**
 * Agent Findings — aggregate review surface
 *
 * One page that shows the active findings from every intelligence agent
 * (Fracture, Compass, Keystone, Meridian, Condition Watch) plus Beacon,
 * grouped by agent. Each agent's report page (/prioritization/agents/<type>) shows a single
 * agent; the ambient "N high-severity findings need review" roll-up notification
 * is cross-agent, so it links here.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldAlert, CheckCircle2, XCircle, Loader2, ExternalLink, ChevronDown, ChevronRight,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useAgentFindingsStore } from '../../stores/prioritization/agentFindingsStore';
import { INTELLIGENCE_AGENTS } from '../../lib/intelligence/agentMeta';
import { LinkifiedText } from '../../components/prioritization/LinkifiedText';
import { FindingAcceptedNextSteps } from '../../components/agents/intelligence/FindingAcceptedNextSteps';
import { ReverseActionButton } from '../../components/reversals/ReverseActionButton';
import { reopenAgentFinding } from '../../lib/reversals/reversalsService';
import { useAgentReviewerAllowed } from '../../hooks/useAgentReviewerAllowed';
import type { AgentFinding } from '../../lib/agentFindingsService';

const SEVERITY_STYLE: Record<string, string> = {
  critical: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
  high: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  low: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  info: 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)]',
};
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

/**
 * May the signed-in person accept or dismiss this agent's findings? Asked once
 * per agent, the same way the per-agent report page asks it (the database's
 * `agent_reviewer_allowed`, which the review trigger enforces — migration 943).
 * Build contract §2.8: the controls are drawn only for someone who may use them.
 */
function AgentReviewGate({
  workspaceId,
  agentType,
  children,
}: {
  workspaceId: string | null;
  agentType: string;
  children: (mayReview: boolean) => ReactNode;
}) {
  const mayReview = useAgentReviewerAllowed(workspaceId, agentType);
  return <>{children(mayReview)}</>;
}

export function AllFindings() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const byAgent = useAgentFindingsStore((s) => s.byAgent);
  const fetchActive = useAgentFindingsStore((s) => s.fetchActive);
  const fetchHistory = useAgentFindingsStore((s) => s.fetchHistory);
  const accept = useAgentFindingsStore((s) => s.accept);
  const dismiss = useAgentFindingsStore((s) => s.dismiss);
  const addNotification = useUIStore((s) => s.addNotification);

  const agents = useMemo(() => Object.values(INTELLIGENCE_AGENTS), []);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [justAccepted, setJustAccepted] = useState<AgentFinding | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    Promise.all(
      agents.flatMap((a) => [fetchActive(workspaceId, a.agentType), fetchHistory(workspaceId, a.agentType, 20)]),
    ).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [workspaceId, agents, fetchActive, fetchHistory]);

  // What this person reviewed — reopenable here as on each agent's report
  // (reopen_agent_finding, mig 746: the reviewer or an admin, with a reason).
  const [showReviewed, setShowReviewed] = useState(false);
  const reviewedByMe = useMemo(
    () =>
      agents
        .flatMap((a) => byAgent[a.agentType]?.history ?? [])
        .filter((f) => f.status !== 'active' && !!userId && f.reviewedByUserId === userId)
        .sort((a, b) => (b.reviewedAt ?? '').localeCompare(a.reviewedAt ?? '')),
    [agents, byAgent, userId],
  );
  const refreshAgent = (agentType: string) => {
    if (!workspaceId) return;
    void fetchActive(workspaceId, agentType);
    void fetchHistory(workspaceId, agentType, 20);
  };

  const groups = useMemo(
    () =>
      agents
        .map((a) => ({ meta: a, findings: byAgent[a.agentType]?.active ?? [] }))
        .filter((g) => g.findings.length > 0),
    [agents, byAgent],
  );
  const total = groups.reduce((n, g) => n + g.findings.length, 0);

  const handleAccept = async (agentType: string, id: string) => {
    if (!userId) return;
    const finding = byAgent[agentType]?.active.find((f) => f.id === id) ?? null;
    setProcessingId(id);
    const result = await accept(agentType, id, userId);
    setProcessingId(null);
    if (!result.success) {
      // The finding stays active in the store — just say why.
      addNotification({
        type: 'error',
        title: 'Finding was not accepted',
        message: result.error ?? 'Try again.',
        duration: 6000,
      });
      return;
    }
    // Accept moves the row out of `active` immediately — capture what to
    // show before it's gone, so the reviewer lands somewhere.
    setJustAccepted(finding);
  };
  const handleDismiss = async (agentType: string, id: string) => {
    if (!userId) return;
    setProcessingId(id);
    const result = await dismiss(agentType, id, userId);
    setProcessingId(null);
    if (!result.success) {
      // The finding stays active in the store — just say why.
      addNotification({
        type: 'error',
        title: 'Finding was not dismissed',
        message: result.error ?? 'Try again.',
        duration: 6000,
      });
    }
  };
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldAlert className="h-4 w-4 text-primary-700" />}
        title="Agent Findings"
        subtitle={
          total > 0
            ? `${total} finding${total === 1 ? '' : 's'} pending review across ${groups.length} agent${groups.length === 1 ? '' : 's'}`
            : 'Findings your intelligence agents have proposed for review.'
        }
      />

      {justAccepted && (
        <FindingAcceptedNextSteps finding={justAccepted} onDismiss={() => setJustAccepted(null)} />
      )}

      {loading ? (
        <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading findings…
        </div>
      ) : total === 0 ? (
        <div className="card text-sm text-[var(--text-ter)]">
          No findings pending review. Run the intelligence agents (Fracture, Compass, Keystone,
          Meridian, Condition Watch) or Beacon to generate findings.
        </div>
      ) : (
        groups.map((g) => {
          const Icon = g.meta.icon;
          const sorted = [...g.findings].sort(
            (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
          );
          return (
            <AgentReviewGate key={g.meta.agentType} workspaceId={workspaceId} agentType={g.meta.agentType}>
              {(mayReview) => (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`flex h-7 w-7 items-center justify-center  ${g.meta.accent.iconBg}`}>
                    <Icon className={`h-4 w-4 ${g.meta.accent.iconText}`} />
                  </span>
                  <h2 className="text-sm font-semibold text-[var(--text-pri)]">{g.meta.name}</h2>
                  <span className="text-xs text-[var(--text-ter)]">{g.findings.length}</span>
                </div>
                <Link
                  to={g.meta.reportHref}
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                >
                  Open {g.meta.name} <ExternalLink className="h-3 w-3" />
                </Link>
              </div>

              <div className="space-y-2">
                {sorted.map((f) => {
                  const isOpen = expanded.has(f.id);
                  const busy = processingId === f.id;
                  return (
                    <div key={f.id} className="card">
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium ${SEVERITY_STYLE[f.severity] ?? SEVERITY_STYLE.info}`}
                        >
                          {f.severity}
                        </span>
                        <div className="min-w-0 flex-1">
                          <button onClick={() => toggle(f.id)} className="flex w-full items-start gap-2 text-left">
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold text-[var(--text-pri)]">{f.title}</span>
                              {f.summary && <span className="mt-0.5 block text-xs text-[var(--text-ter)]">{f.summary}</span>}
                            </span>
                            {isOpen ? (
                              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
                            ) : (
                              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
                            )}
                          </button>
                          {isOpen && f.detail && (
                            <LinkifiedText
                              text={f.detail}
                              className="mt-2 whitespace-pre-wrap border-t border-[var(--rule)] pt-2 text-sm text-[var(--text-sec)]"
                            />
                          )}
                          {mayReview ? (
                            <div className="mt-3 flex items-center gap-2">
                              <button
                                onClick={() => handleAccept(f.agentType, f.id)}
                                disabled={busy || !userId}
                                className="inline-flex items-center gap-1 bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                              >
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Accept
                              </button>
                              <button
                                onClick={() => handleDismiss(f.agentType, f.id)}
                                disabled={busy || !userId}
                                className="inline-flex items-center gap-1 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
                              >
                                <XCircle className="h-3.5 w-3.5" /> Dismiss
                              </button>
                            </div>
                          ) : (
                            <p className="mt-3 text-xs text-[var(--text-ter)]">
                              Accepting or dismissing {g.meta.name} findings takes one of its review roles or a
                              workspace admin — the roles are set in{' '}
                              <Link to="/prioritization/settings/agents" className="underline">Agent settings</Link>.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
              )}
            </AgentReviewGate>
          );
        })
      )}

      {reviewedByMe.length > 0 && (
        <section className="space-y-2">
          <button
            type="button"
            onClick={() => setShowReviewed((v) => !v)}
            className="flex items-center gap-1 text-sm font-semibold text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            {showReviewed ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Reviewed by you ({reviewedByMe.length})
          </button>
          {showReviewed &&
            reviewedByMe.map((f) => (
              <div key={f.id} className="card flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--text-pri)]">{f.title}</p>
                  <p className="mt-0.5 text-xs text-[var(--text-ter)]">
                    {f.status === 'accepted' ? 'Accepted' : 'Dismissed'}
                    {f.reviewedAt ? ` on ${new Date(f.reviewedAt).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <ReverseActionButton
                  label="Reopen"
                  confirmTitle="Reopen finding"
                  explain={`This will return "${f.title}" to the active list for review again.`}
                  run={(reason) => reopenAgentFinding(f.id, reason)}
                  onDone={() => refreshAgent(f.agentType)}
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-1 !text-xs"
                />
              </div>
            ))}
        </section>
      )}
    </div>
  );
}

export default AllFindings;
